"use client";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { cloneElement, forwardRef, isValidElement, useCallback, useState } from "react";
import { cleanProps, cn } from "../../utils/index.js";
import { Badge } from "../Badge/index.js";
import { CdnIcon } from "../CdnIcon/index.js";
import { ContentfulImgOptimizer } from "../ContentfulImgOptimizer/index.js";
import { Heading } from "../Heading/index.js";
import { Highlight } from "../Highlight/index.js";
import { HorizonContentfulRichText } from "../HorizonContentfulRichText/index.js";
import { Image } from "../Image/index.js";
import { LogoImg } from "../LogoImg/index.js";
import { plainAVariants } from "../PlainA/PlainA.variants.js";
import { plainButtonVariants } from "../PlainButton/PlainButton.variants.js";
import { PromoCode } from "../PromoCode/index.js";
import { P } from "../Text/index.js";
import { kenBurnsEffectClassMap } from "../animations/KenBurnsEffect/kenBurnsEffectClasses.js";
import { mediaCardVariants } from "./MediaCard.variants.js";
const KEN_BURNS_CENTER_LONG = kenBurnsEffectClassMap.find((effect)=>'Center Long' === effect.name);
const plainTextStyleIconSizes = {
    body1: '24',
    body2: '20',
    body3: '16',
    heading4: '24',
    heading5: '20',
    heading6: '16'
};
function MediaCardPlainACTAPresentation({ children, className, iconLeft, iconRight, slots = {
    buttonSlot: '',
    textSlot: '',
    iconSlot: ''
}, ...props }) {
    const { buttonSlot, textSlot, iconSlot } = plainAVariants(props);
    const classes = cn(buttonSlot(), className ?? slots.buttonSlot);
    const iconSlotClasses = cn(iconSlot(), slots.iconSlot);
    const textSlotClasses = cn(textSlot(), slots.textSlot);
    const iconSize = plainTextStyleIconSizes[props.textStyle ?? 'body2'];
    return /*#__PURE__*/ jsx("div", {
        className: classes,
        ...cleanProps(props, [
            'type',
            'textStyle',
            'emphasis',
            'variant',
            'active'
        ]),
        children: /*#__PURE__*/ jsxs("div", {
            className: "align-center inline-flex flex-row items-center justify-center gap-1",
            children: [
                iconLeft && /*#__PURE__*/ jsx(CdnIcon, {
                    className: iconSlotClasses,
                    iconName: iconLeft,
                    size: iconSize,
                    title: 'string' == typeof children ? children : void 0
                }),
                /*#__PURE__*/ jsx("div", {
                    className: textSlotClasses,
                    children: children
                }),
                iconRight && /*#__PURE__*/ jsx(CdnIcon, {
                    className: iconSlotClasses,
                    iconName: iconRight,
                    size: iconSize,
                    title: 'string' == typeof children ? children : void 0
                })
            ]
        })
    });
}
function MediaCardPlainButtonCTAPresentation({ children, className, iconLeft, iconRight, loading, slots = {
    buttonSlot: '',
    textSlot: '',
    iconSlot: ''
}, ...props }) {
    const { buttonSlot, textSlot, iconSlot } = plainButtonVariants({
        ...props,
        disabled: props.disabled || loading
    });
    const classes = cn(buttonSlot(), className ?? slots.buttonSlot);
    const iconSlotClasses = cn(iconSlot(), slots.iconSlot);
    const textSlotClasses = cn(textSlot(), slots.textSlot);
    const iconSize = plainTextStyleIconSizes[props.textStyle || 'body2'];
    return /*#__PURE__*/ jsxs("div", {
        className: classes,
        ...cleanProps(props, [
            'type',
            'textStyle',
            'emphasis',
            'disabled'
        ]),
        children: [
            loading && /*#__PURE__*/ jsx(CdnIcon, {
                className: cn('animate-spin', iconSlotClasses),
                iconName: "refresh",
                size: iconSize
            }),
            iconLeft && !loading && /*#__PURE__*/ jsx(CdnIcon, {
                className: iconSlotClasses,
                iconName: iconLeft,
                size: iconSize
            }),
            /*#__PURE__*/ jsx("div", {
                className: textSlotClasses,
                children: children
            }),
            iconRight && /*#__PURE__*/ jsx(CdnIcon, {
                className: iconSlotClasses,
                iconName: iconRight,
                size: iconSize
            })
        ]
    });
}
function isLogoImgSlotProps(value) {
    return 'object' == typeof value && null !== value && 'src' in value && 'alt' in value && !('$$typeof' in value);
}
function isMediaCardImageSlotProps(value) {
    return 'object' == typeof value && null !== value && 'alt' in value && !('contentfulUrl' in value) && !('$$typeof' in value);
}
function isMediaCardContentfulImageSlotProps(value) {
    return 'object' == typeof value && null !== value && 'contentfulUrl' in value && 'alt' in value && !('$$typeof' in value);
}
const LOGO_IMG_PRESETS = {
    topLeft: {
        display: 'topLeft',
        emphasis: 'bold',
        size: 'md',
        clearSpace: true
    },
    inline: {
        display: 'inline',
        emphasis: 'bold',
        size: 'md',
        clearSpace: true
    }
};
function resolveMediaCardLogoColorMode(emphasis, explicitColorMode) {
    if (void 0 !== explicitColorMode) return explicitColorMode;
    return 'bold' === emphasis ? 'white' : 'original';
}
const DEFAULT_MEDIA_IMAGE_PROPS = {
    ratio: '16/9',
    width: 'full',
    height: 'full'
};
function isNonEmptyMediaUrl(url) {
    return 'string' == typeof url && url.trim().length > 0;
}
const MediaCard_MediaCard = /*#__PURE__*/ forwardRef(function({ role = 'link', href, onClick, anchorProps, buttonProps, ariaLabel, mediaSlot, logoImgSlot, logoPosition = 'topLeft', badgeSlot, adSlot, eyebrowSlot, headingSlot, contentfulRichTextSlot, bodySlot, callToActionText, callToActionSlot, ctaPlainAProps, ctaPlainButtonProps, showPromoCode = false, promoCodeProps, promoCodeSlot, className }, ref) {
    const [isHovered, setIsHovered] = useState(false);
    const handleMouseEnter = useCallback(()=>{
        setIsHovered(true);
    }, []);
    const handleMouseLeave = useCallback(()=>{
        setIsHovered(false);
    }, []);
    const kenBurnsClass = isHovered ? KEN_BURNS_CENTER_LONG?.in : KEN_BURNS_CENTER_LONG?.out;
    const { rootSlot, clickableSlot, mediaWrapperSlot, mediaImageSlot, overlaysSlot, logoImgSlot: logoImgSlotClass, badgeSlot: badgeSlotClass, adSlot: adSlotClass, contentSlot, eyebrowSlot: eyebrowSlotClass, headingSlot: headingSlotClass, bodySlot: bodySlotClass, ctaSlot, promoSlot } = mediaCardVariants({
        logoPosition,
        hasLogoForBadge: Boolean(logoImgSlot)
    });
    const renderMediaSlot = ()=>{
        if (!mediaSlot) return /*#__PURE__*/ jsx(Image, {
            alt: "",
            ratio: DEFAULT_MEDIA_IMAGE_PROPS.ratio,
            width: DEFAULT_MEDIA_IMAGE_PROPS.width,
            height: DEFAULT_MEDIA_IMAGE_PROPS.height
        });
        if (isMediaCardContentfulImageSlotProps(mediaSlot)) {
            const { contentfulUrl, alt, ratio = DEFAULT_MEDIA_IMAGE_PROPS.ratio, width = DEFAULT_MEDIA_IMAGE_PROPS.width, height = DEFAULT_MEDIA_IMAGE_PROPS.height, fixedWidth, fixedHeight, widthCap } = mediaSlot;
            if (!isNonEmptyMediaUrl(contentfulUrl)) return /*#__PURE__*/ jsx(Image, {
                alt: alt,
                ratio: ratio,
                width: width,
                height: height
            });
            return /*#__PURE__*/ jsx(ContentfulImgOptimizer, {
                contentfulUrl: contentfulUrl,
                alt: alt,
                ratio: ratio,
                width: width,
                height: height,
                fixedWidth: fixedWidth,
                fixedHeight: fixedHeight,
                widthCap: widthCap
            });
        }
        if (isMediaCardImageSlotProps(mediaSlot)) {
            const { src, alt, ratio = DEFAULT_MEDIA_IMAGE_PROPS.ratio, width = DEFAULT_MEDIA_IMAGE_PROPS.width, height = DEFAULT_MEDIA_IMAGE_PROPS.height } = mediaSlot;
            return /*#__PURE__*/ jsx(Image, {
                src: src,
                alt: alt,
                ratio: ratio,
                width: width,
                height: height
            });
        }
        return mediaSlot;
    };
    const renderLogoImg = ()=>{
        if (!logoImgSlot) return null;
        if (isLogoImgSlotProps(logoImgSlot)) {
            const defaultPreset = 'center' === logoPosition ? 'inline' : 'topLeft';
            const { src, alt, preset = defaultPreset, colorMode: explicitColorMode, ...logoOverrides } = logoImgSlot;
            const presetProps = LOGO_IMG_PRESETS[preset];
            const merged = {
                ...presetProps,
                ...logoOverrides
            };
            const colorMode = resolveMediaCardLogoColorMode(merged.emphasis, explicitColorMode);
            return /*#__PURE__*/ jsx("div", {
                className: logoImgSlotClass(),
                children: /*#__PURE__*/ jsx(LogoImg, {
                    src: src,
                    alt: alt,
                    ...merged,
                    colorMode: colorMode
                })
            });
        }
        return /*#__PURE__*/ jsx("div", {
            className: logoImgSlotClass(),
            children: logoImgSlot
        });
    };
    const renderBadge = ()=>{
        if (!badgeSlot) return null;
        const badgeContent = /*#__PURE__*/ isValidElement(badgeSlot) && badgeSlot.type === Badge && void 0 === badgeSlot.props.emphasis ? /*#__PURE__*/ cloneElement(badgeSlot, {
            emphasis: 'regular'
        }) : badgeSlot;
        return /*#__PURE__*/ jsx("div", {
            className: badgeSlotClass(),
            children: badgeContent
        });
    };
    const renderAd = ()=>{
        if (!adSlot) return null;
        return /*#__PURE__*/ jsx("div", {
            className: adSlotClass(),
            children: /*#__PURE__*/ jsx(Highlight, {
                textStyle: "disclaimer",
                palette: "neutral",
                children: adSlot
            })
        });
    };
    const renderCTA = ()=>{
        if (callToActionSlot) return /*#__PURE__*/ jsx("div", {
            className: ctaSlot(),
            children: callToActionSlot
        });
        if (!callToActionText) return null;
        if ('link' === role) return /*#__PURE__*/ jsx("div", {
            className: ctaSlot(),
            children: /*#__PURE__*/ jsx(MediaCardPlainACTAPresentation, {
                textStyle: "body2",
                type: "primary",
                ...ctaPlainAProps,
                children: callToActionText
            })
        });
        return /*#__PURE__*/ jsx("div", {
            className: ctaSlot(),
            children: /*#__PURE__*/ jsx(MediaCardPlainButtonCTAPresentation, {
                textStyle: "body2",
                type: "primary",
                ...ctaPlainButtonProps,
                children: callToActionText
            })
        });
    };
    const renderBody = ()=>{
        if (contentfulRichTextSlot) return /*#__PURE__*/ jsx("div", {
            className: bodySlotClass(),
            children: /*#__PURE__*/ jsx(HorizonContentfulRichText, {
                document: contentfulRichTextSlot,
                variant: "marketing"
            })
        });
        if (bodySlot) return /*#__PURE__*/ jsx(P, {
            textStyle: "body2",
            className: bodySlotClass(),
            children: bodySlot
        });
        return null;
    };
    const innerContent = /*#__PURE__*/ jsxs(Fragment, {
        children: [
            /*#__PURE__*/ jsxs("div", {
                className: mediaWrapperSlot(),
                children: [
                    /*#__PURE__*/ jsx("div", {
                        className: cn(mediaImageSlot(), kenBurnsClass),
                        children: renderMediaSlot()
                    }),
                    /*#__PURE__*/ jsxs("div", {
                        className: overlaysSlot(),
                        children: [
                            renderLogoImg(),
                            renderBadge(),
                            renderAd()
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ jsxs("div", {
                className: contentSlot(),
                children: [
                    eyebrowSlot && /*#__PURE__*/ jsx(P, {
                        className: eyebrowSlotClass(),
                        palette: "neutral",
                        shade: "13",
                        textStyle: "body3",
                        children: eyebrowSlot
                    }),
                    headingSlot && /*#__PURE__*/ jsx("div", {
                        className: headingSlotClass(),
                        children: /*#__PURE__*/ jsx(Heading, {
                            as: "h3",
                            textStyle: "heading4",
                            children: headingSlot
                        })
                    }),
                    renderBody(),
                    renderCTA()
                ]
            })
        ]
    });
    const renderClickableSlot = ()=>{
        if ('link' === role) return /*#__PURE__*/ jsx("a", {
            href: href,
            "aria-label": ariaLabel,
            onClick: onClick,
            className: clickableSlot(),
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            ...anchorProps,
            children: innerContent
        });
        return /*#__PURE__*/ jsx("button", {
            type: "button",
            "aria-label": ariaLabel,
            onClick: onClick,
            className: clickableSlot(),
            onMouseEnter: handleMouseEnter,
            onMouseLeave: handleMouseLeave,
            ...buttonProps,
            children: innerContent
        });
    };
    const renderPromoCode = ()=>{
        if (!showPromoCode) return null;
        if (promoCodeSlot) return /*#__PURE__*/ jsx("div", {
            className: promoSlot(),
            children: promoCodeSlot
        });
        if (promoCodeProps) return /*#__PURE__*/ jsx("div", {
            className: promoSlot(),
            children: /*#__PURE__*/ jsx(PromoCode, {
                ...promoCodeProps
            })
        });
        return null;
    };
    return /*#__PURE__*/ jsxs("div", {
        ref: ref,
        className: cn(rootSlot(), className),
        children: [
            renderClickableSlot(),
            renderPromoCode()
        ]
    });
});
MediaCard_MediaCard.displayName = 'MediaCard';
export { MediaCard_MediaCard as MediaCard };
